import React, { ElementType } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, styled } from '@mui/material'
import { useEnvironment } from '@hooks/useEnvironment'
import { SubmitError, SubmitErrorType } from '@typesApp/SubmitError'
import { FooterAction } from '@components/ui/Modal/FooterAction'
import { Alert } from '@components/ui-css/Alert'
import { injectDataAttributes } from '@utils/dataAttributes'
import { BasicErrorModal } from './BasicErrorModal'

interface ContentItem {
  value: string
  items?: string[]
}

interface ClaimErrorModalProps {
  onClose: () => void
  error: SubmitError
}

export const ClaimErrorModal: React.FC<ClaimErrorModalProps> = props => {
  const { error, onClose } = props

  const modals = {
    LIMIT_REACHED: LimitReachedModal,
    CLAIM_ACKNOWLEDGED: AcknowledgedModal,
    TIMEOUT_RETRY: TimeoutRetryModal,
    SUBMIT_CLAIM_NO_RETRY_ERROR: NoRetryErrorModal,
  } as Record<SubmitErrorType, ElementType>

  const ErrorModal = modals[error.type]
  return ErrorModal ? <ErrorModal onClose={onClose} /> : null
}

interface LocalModalProps {
  onClose: () => void
}

const AcknowledgedModal: React.FC<LocalModalProps> = props => {
  const { onClose } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'SubmitError.acknowledged' })

  return (
    <BasicErrorModal
      open={true}
      onClose={onClose}
      title={t('title')}
      footer={
        <FooterAction
          onClick={onClose}
          renderAs="button"
          label={t('proceed-cta')}
          icon="arrow-right-icon"
          dataAttributes={injectDataAttributes('modal.proceedToCheckout')}
        />
      }
    >
      <Box>
        <Alert severity={'warning'} message={t('message')} />
      </Box>
    </BasicErrorModal>
  )
}

const LimitReachedModal: React.FC<LocalModalProps> = props => {
  const { onClose } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'SubmitError.limitReached' })

  return (
    <BasicErrorModal
      open={true}
      onClose={onClose}
      title={t('title')}
      message={t('message')}
      footer={
        <FooterAction
          onClick={onClose}
          renderAs="button"
          label={t('close-cta')}
          icon="arrow-right-icon"
          dataAttributes={injectDataAttributes('modal.close')}
        />
      }
    />
  )
}

const TimeoutRetryModal: React.FC<LocalModalProps> = props => {
  const { onClose } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'SubmitError.timeoutRetry' })

  const items = t('content', { returnObjects: true }) as ContentItem[]

  return (
    <BasicErrorModal
      open={true}
      onClose={onClose}
      title={t('title')}
      message={t('message')}
      footer={
        <FooterAction
          onClick={onClose}
          renderAs="button"
          label={t('close-cta')}
          icon="arrow-right-icon"
          dataAttributes={injectDataAttributes('modal.close')}
        />
      }
    >
      <StyledOrderedList>
        {items.map((item, index) => {
          return (
            <li key={index}>
              <Typography>{item.value}</Typography>
              {item?.items && (
                <StyledAlphaOrderedList>
                  {item?.items.map((value, index) => (
                    <li key={index}>
                      <Typography>{value}</Typography>
                    </li>
                  ))}
                </StyledAlphaOrderedList>
              )}
            </li>
          )
        })}
      </StyledOrderedList>
    </BasicErrorModal>
  )
}

const NoRetryErrorModal: React.FC<LocalModalProps> = props => {
  const { onClose } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'SubmitError.submitClaim' })

  return (
    <BasicErrorModal
      open={true}
      onClose={onClose}
      title={t('title')}
      message={t('message')}
      footer={
        <FooterAction
          onClick={onClose}
          renderAs="button"
          label={t('proceed-cta')}
          icon="arrow-right-icon"
          dataAttributes={injectDataAttributes('modal.proceedToCheckout')}
        />
      }
    />
  )
}

const StyledOrderedList = styled('ol', {
  name: 'SubmitError',
  slot: 'TimeoutRetry',
})(({ theme }) => ({
  paddingInlineStart: theme.spacing(5),
}))

const StyledAlphaOrderedList = styled(StyledOrderedList, {
  name: 'SubmitError',
  slot: 'TimeoutRetry',
})(() => ({
  listStyleType: 'lower-alpha',
}))
